import { useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Loader2, Globe, UserPlus } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import api from "@/lib/api";

export default function Signup() {
  const { t, i18n } = useTranslation();
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ full_name: "", email: "", company: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [accepted, setAccepted] = useState(false);
  const es = i18n.language?.startsWith("es");

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!accepted) { setError(es ? "Debes aceptar los términos para continuar." : "You must accept the terms to continue."); return; }
    setLoading(true);
    try {
      await api.post("/auth/signup", form);
      await login(form.email, form.password);
      navigate("/", { replace: true });
    } catch (err) {
      setError(err?.response?.data?.detail || (es ? "No se pudo crear la cuenta" : "Could not create account"));
    } finally { setLoading(false); }
  };

  const toggleLang = () => i18n.changeLanguage(es ? "en" : "es");

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-[#F5F7FA]" data-testid="signup-page">
      <div className="relative hidden md:flex flex-col justify-between p-10 text-white overflow-hidden"
           style={{background: "linear-gradient(135deg, #003366 0%, #002244 60%, #001A33 100%)"}}>
        <div className="absolute inset-0 opacity-40" style={{
          backgroundImage:"linear-gradient(to right, rgba(255,255,255,0.06) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.06) 1px, transparent 1px)",
          backgroundSize: "24px 24px",
        }} />
        <div className="absolute -right-40 -bottom-40 w-[500px] h-[500px] rounded-full bg-[#2DD4BF]/20 blur-3xl" />
        <div className="relative z-10"><Logo className="text-white [&_span]:text-white [&_.text-slate-500]:text-slate-300" /></div>
        <div className="relative z-10 max-w-md">
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-[#EA580C] mb-4">AI ESTIMATOR · ELECTRICAL · LOW VOLTAGE</div>
          <h1 className="font-display text-4xl font-bold leading-tight tracking-tight">{es ? "Crea tu cuenta IQ-APP" : "Create your IQ-APP account"}</h1>
          <p className="mt-6 text-slate-300 text-sm leading-relaxed">{es ? "Comienza a generar BOMs, planos anotados y site surveys con IA en segundos." : "Start generating BOMs, annotated blueprints and site surveys with AI in seconds."}</p>
        </div>
        <div className="relative z-10 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">IQES LLC · FL · © 2026</div>
      </div>

      <div className="flex flex-col">
        <div className="flex justify-end p-4">
          <button onClick={toggleLang} data-testid="toggle-language-btn" className="flex items-center gap-2 px-3 py-1.5 rounded-xl text-xs font-mono uppercase tracking-wider text-slate-600 hover:bg-slate-100">
            <Globe size={13} />{es ? "ES" : "EN"}
          </button>
        </div>
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="w-full max-w-sm">
            <div className="mb-8">
              <div className="text-xs font-mono uppercase tracking-[0.2em] text-slate-500 mb-2">IQES · IQ-APP · SIGN UP</div>
              <h2 className="font-display text-2xl font-bold text-slate-900 tracking-tight">{es ? "Registro" : "Sign up"}</h2>
              <p className="text-sm text-slate-600 mt-1">{es ? "Prueba gratis, sin tarjeta." : "Free trial, no card required."}</p>
            </div>
            <form onSubmit={submit} className="space-y-3" data-testid="signup-form">
              {[
                { key: "full_name", label: es ? "Nombre completo" : "Full name", type: "text", required: true, testid: "signup-name" },
                { key: "email", label: es ? "Correo electrónico" : "Email", type: "email", required: true, testid: "signup-email" },
                { key: "company", label: es ? "Empresa (opcional)" : "Company (optional)", type: "text", testid: "signup-company" },
                { key: "password", label: es ? "Contraseña (min 6)" : "Password (min 6)", type: "password", required: true, testid: "signup-password" },
              ].map((f) => (
                <div key={f.key}>
                  <label className="text-xs font-mono uppercase tracking-wider text-slate-500 block mb-1">{f.label}</label>
                  <input data-testid={f.testid} type={f.type} required={f.required}
                    value={form[f.key]} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                    className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#003366]/30 focus:border-[#003366] font-mono" />
                </div>
              ))}
              <label className="flex items-start gap-2 text-xs text-slate-700 mt-3" data-testid="accept-terms-label">
                <input type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} className="mt-0.5" data-testid="accept-terms" />
                <span>
                  {es ? "Acepto los " : "I accept the "}
                  <Link to="/legal/terms" className="text-[#003366] underline">{es ? "Términos de Uso" : "Terms of Use"}</Link>
                  {es ? " y la " : " and "}
                  <Link to="/legal/privacy" className="text-[#003366] underline">{es ? "Política de Privacidad" : "Privacy Policy"}</Link>.
                </span>
              </label>
              {error && (
                <div data-testid="signup-error" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-3 py-2 font-mono">{error}</div>
              )}
              <button data-testid="signup-submit-btn" type="submit" disabled={loading}
                className="w-full bg-[#003366] text-white hover:bg-[#002244] rounded-xl px-4 py-2.5 font-medium text-sm transition-colors disabled:opacity-60 flex items-center justify-center gap-2 mt-3">
                {loading ? <Loader2 size={14} className="animate-spin" /> : <UserPlus size={14} />}
                {es ? "Crear cuenta" : "Create account"}
              </button>
              <div className="text-xs text-slate-600 font-mono text-center pt-3">
                {es ? "¿Ya tienes cuenta? " : "Already have an account? "}
                <Link to="/login" className="text-[#003366] underline">{es ? "Inicia sesión" : "Sign in"}</Link>
              </div>
            </form>
          </div>
        </div>
        <div className="p-4 text-center text-[10px] font-mono uppercase tracking-[0.2em] text-slate-400">Part of IQES ecosystem · CRM · IQ-APP · ERP</div>
      </div>
    </div>
  );
}
